import React, { Suspense } from "react";
import { ThemeProvider } from "styled-components";

import { I18nextProvider, Trans, useTranslation } from "react-i18next";
import i18n from "../i18n";
import { getThemeColors, getThemeMUIPalette } from "./Common/colors";
import { CssBaseline } from "@mui/material";
import { getClientType } from "../utils";
import { ErrorBoundary } from "@sentry/react";

// Modify react behavior so we don't get exceptions when using Google Chrome Translate
import googleTranslateHack from "../google-translate-hack";
import PortalProvider from "../Providers/PortalProvider";
import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider as MUIThemeProvider,
} from "@mui/material/styles";
import { StylesProvider, createGenerateClassName } from "@mui/styles";
import {
  AssetResolverProvider,
  defaultAssetResolver,
} from "@/react/Webshop/providers/AssetResolver";
import { WebshopApiClientProvider } from "@/react/Webshop/providers/WebshopApiClientProvider";
import { ShoppingCartApiClientProvider } from "@/react/Webshop/providers/ShoppingCartClientProvider";
import StylesThemeProvider from "@mui/styles/ThemeProvider";

googleTranslateHack();

let ID = 0;

// Make JSS classnames stable and avoid collisions between bundles/microfrontends.
const generateClassName = createGenerateClassName({
  seed: "mui-jss",
  productionPrefix: "mui-jss",
});

const createThemeFromPalette = (palette) =>
  createTheme({
    palette: palette,
    typography: {
      body2: {
        fontSize: "0.937rem",
      },
    },
    clientType: getClientType(),
    overrides: {
      MuiButton: {
        root: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
  });

const theme = createThemeFromPalette(getThemeMUIPalette());

console.log("Mui theme", theme);

class AppWrapper extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      MUITheme: theme,
      theme: {
        mode: "light",
        margin: {
          default: "15px",
          large: "32px",
        },
        colors: getThemeColors(),
        clientType: getClientType(),
        MUITheme: theme,
      },
    };
    this.ID = ID++;
  }

  componentDidMount() {
    // Hack, use jQuery custom components to update the theme:
    const self = this;
    if (typeof jQuery != "undefined") {
      jQuery(document).on(
        "colorUpdate",
        {
          foo: "bar",
        },
        function (event, colors, arg2) {
          console.log("Color update", self, colors); //
          self.setState((state) => {
            state.theme.colors = colors.colors;

            // Need updated obj to refresh the components:
            state.theme = JSON.parse(JSON.stringify(state.theme));
            state.MUITheme = createThemeFromPalette(colors.MUIColors);
            return state;
          });
        },
      );
    }
  }

  render() {
    console.log("THEMEss", this.ID, this.state.theme);
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <WebshopApiClientProvider>
          <ShoppingCartApiClientProvider>
            <PortalProvider>
              <I18nextProvider i18n={i18n}>
                <TranslationWrapper>
                  <MyErrorBoundary>
                    <AssetResolverProvider resolver={defaultAssetResolver}>
                      <StylesProvider
                        injectFirst
                        generateClassName={generateClassName}
                      >
                        <StyledEngineProvider>
                          <MUIThemeProvider theme={this.state.MUITheme}>
                            <StylesThemeProvider theme={this.state.MUITheme}>
                              <CssBaseline />
                              <ThemeProvider theme={this.state.theme}>
                                {this.props.children}
                              </ThemeProvider>
                            </StylesThemeProvider>
                          </MUIThemeProvider>
                        </StyledEngineProvider>
                      </StylesProvider>
                    </AssetResolverProvider>
                  </MyErrorBoundary>
                </TranslationWrapper>
              </I18nextProvider>
            </PortalProvider>
          </ShoppingCartApiClientProvider>
        </WebshopApiClientProvider>
      </Suspense>
    );
  }
}

const TranslationWrapper = ({ children }) => {
  // It's peak season -24, and I don't want to upgrade i18next.
  // There is a bug if the first translation is a Trans component,
  // it will claim i18 next is not initialized.
  // Calling the hook seem to be enough to initialize it
  useTranslation();

  return children;
};

const MyErrorBoundary = ({ children }) => {
  if (process.env.NODE_ENV !== "production") {
    return <>{children}</>;
  }

  return <ErrorBoundary fallback={<ErrorMessage />}>{children}</ErrorBoundary>;
};

const ErrorMessage = ({ error, componentStack, resetError }) => {
  const email = window.NTLF_CONFIG?.portal?.contactInfo?.email || "";

  return (
    <div>
      <h1>
        <Trans>An error occurred</Trans>
      </h1>
      <p>
        <Trans>Please refresh the page to try again.</Trans>
      </p>
      <p>
        <Trans>
          If you keep getting the error, please let us know at{" "}
          <a href={`mailto:${email}`}>{{ errorEmailAddress: email }}</a>
        </Trans>
      </p>
    </div>
  );
};

export default AppWrapper;
